/**
 * PII inference. The `openai/privacy-filter` model uses a custom architecture
 * (`OpenAIPrivacyFilterForTokenClassification`) that transformers.js's
 * `pipeline()` doesn't have a built-in handler for, so we drive ONNX Runtime
 * Web ourselves: tokenize with `AutoTokenizer.from_pretrained`, fetch the
 * `.onnx` graph + `.onnx_data` external weights with progress callbacks,
 * run inference, then apply a constrained BIOES Viterbi decode and span
 * walker — exactly what `OnnxPiiFilter.kt` + `ViterbiDecoder.kt` +
 * `SpanAssembler.kt` do on Android.
 */

import { AutoTokenizer, env, type PreTrainedTokenizer } from "@huggingface/transformers";
import type * as ort from "onnxruntime-web";
import { loadOrt, type OrtNamespace } from "./ort-runtime.js";
import { variantById } from "./models.js";
import { fetchResumable } from "./downloadcache.js";
import { assembleSpans, type CharSpan } from "./spanassembler.js";
import type { VariantId } from "./settings.js";
import { viterbiDecode } from "./viterbi.js";

env.allowLocalModels = false;
env.useBrowserCache = true;

const MODEL_ID = "openai/privacy-filter";
/**
 * Where to fetch model files from. Defaults to Hugging Face. The dev-mode
 * shortcut to a `/test/onnx/` mirror is opt-in — set
 * `localStorage.redact_dev_local_models = "1"` to use it. Avoids the 800 MB
 * round-trip on every page reload when iterating, without being magic
 * behaviour for normal users on localhost.
 */
function modelBase(): string {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("redact_dev_local_models") === "1") {
      return location.origin + "/test";
    }
  } catch {
    /* ignore */
  }
  return `https://huggingface.co/${MODEL_ID}/resolve/main`;
}
const CACHE_FLAG_KEY = (id: VariantId) => `redact_pii_cached_${id}`;
const LABELS_URL = "/labels.json";

export interface PiiSpan {
  start: number;
  endExclusive: number;
  label: string;
}

export type PiiProgress =
  | { stage: "fetch_tokenizer" }
  | { stage: "fetch_graph"; loaded: number; total: number }
  | { stage: "fetch_weights"; file: string; loaded: number; total: number }
  | { stage: "compiling" }
  | { stage: "ready" };

export type PiiProgressListener = (p: PiiProgress) => void;

interface LoadedFilter {
  variant: VariantId;
  session: ort.InferenceSession;
  tokenizer: PreTrainedTokenizer;
  labels: string[];
  /** ORT namespace whose .wasm matched the current browser's WebGPU
   *  status. Stored so `findSpans` can construct Tensors with the same
   *  build the session was created with. */
  ort: OrtNamespace;
}

let active: LoadedFilter | null = null;
let loading: Promise<LoadedFilter> | null = null;

export function isVariantCached(id: VariantId): boolean {
  try {
    return localStorage.getItem(CACHE_FLAG_KEY(id)) === "1";
  } catch {
    return false;
  }
}

// EP list now comes from ort-runtime.ts based on which wasm flavor was
// loaded. With the asyncify (webgpu) build we use ["webgpu", "wasm"] —
// every contrib op the graph uses has a native WebGPU kernel, so the
// wasm fall-back is just defensive. With the /all (jsep) build we use
// ["wasm"] — the CPU contrib kernels handle everything.

/**
 * Fetch + compile the requested variant. Idempotent — repeat calls for the
 * same variant return the cached session. Records a cache flag in
 * localStorage so the catalog UI can render "Installed" without a probe.
 */
export async function loadPii(
  id: VariantId,
  listener?: PiiProgressListener,
): Promise<LoadedFilter> {
  if (active && active.variant === id) {
    listener?.({ stage: "ready" });
    return active;
  }
  if (loading) return loading;

  const v = variantById(id);
  if (!v) throw new Error(`Unknown variant: ${id}`);

  loading = (async () => {
    // Phase timings — read by the runtime status block. Each phase
    // records start + end via performance.mark, and a duration via
    // performance.measure. Cleared on each load so the most recent
    // run wins.
    const T0 = performance.now();
    const t = (name: string) => {
      const ms = Math.round(performance.now() - T0);
      try { performance.mark(`pii:${name}`); } catch { /* ignore */ }
      // eslint-disable-next-line no-console
      console.log(`[pii] ${name} @ ${ms}ms`);
      return ms;
    };
    t("start");
    listener?.({ stage: "fetch_tokenizer" });
    const tokenizer = await AutoTokenizer.from_pretrained(MODEL_ID, {
      progress_callback: () => undefined,
    });
    t("tokenizer-ready");

    // Fetch every model artifact for this variant. The first non-tokenizer
    // file is always the graph; subsequent are the external weight blobs.
    const graphFile = v.files.find((f) => f.kind === "graph");
    const weightFiles = v.files.filter((f) => f.kind === "weights");
    if (!graphFile) throw new Error(`Variant ${id} has no graph file`);

    const base = modelBase();
    const graphUrl = `${base}/onnx/${graphFile.localName}`;

    // Pre-fetch the weights so the loader UI can show progress (ORT itself
    // would fetch them silently otherwise). Hand them to ORT through
    // `externalData` as `{ data: Uint8Array, path }` entries. The
    // downloader streams into OPFS, so a tab close mid-download
    // resumes from the byte offset already on disk on the next
    // session instead of restarting from zero.
    const externalData: Array<{ path: string; data: Uint8Array }> = [];
    t("graph-fetch-start");
    for (const w of weightFiles) {
      const bytes = await fetchResumable(
        `${base}/onnx/${w.localName}`,
        `${id}__${w.localName}`,
        w.bytes,
        (loaded, total) =>
          listener?.({
            stage: "fetch_weights",
            file: w.localName,
            loaded,
            total: total || w.bytes,
          }),
      );
      externalData.push({ path: w.localName, data: bytes });
    }
    t("weights-fetched");

    listener?.({ stage: "compiling" });

    const { ort: ortLib, executionProviders } = await loadOrt();

    let session: ort.InferenceSession;
    try {
      // Hand the graph URL plus the pre-fetched external data. ORT-Web's
      // own fetcher does not chase a sibling URL when the graph itself is
      // loaded by URL — it expects an explicit `externalData` array, with
      // each `path` matching the reference inside the ONNX graph.
      session = await ortLib.InferenceSession.create(graphUrl, {
        executionProviders,
        graphOptimizationLevel: "all",
        externalData,
      } as ort.InferenceSession.SessionOptions);
      t("session-created");
    } catch (e) {
      // ORT-Web sometimes throws a numeric heap pointer instead of an
      // Error with a message. Try to fish a string out of the wasm
      // module's UTF8 helper if it's exposed; otherwise stringify.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ortMod = (globalThis as unknown as { Module?: { UTF8ToString?: (n: number) => string } }).Module;
      let msg = "";
      if (typeof e === "number" && ortMod?.UTF8ToString) {
        try { msg = ortMod.UTF8ToString(e); } catch { /* ignore */ }
      }
      throw new Error(`InferenceSession.create: ${msg || (e as Error)?.message || JSON.stringify(e)}`);
    }

    const labels = await fetch(LABELS_URL)
      .then((r) => r.json())
      .then((arr) => arr as string[]);

    try {
      localStorage.setItem(CACHE_FLAG_KEY(id), "1");
    } catch {
      /* private mode */
    }
    listener?.({ stage: "ready" });

    const loaded: LoadedFilter = { variant: id, session, tokenizer, labels, ort: ortLib };
    active = loaded;
    return loaded;
  })();

  try {
    return await loading;
  } catch (e) {
    // Surface the failure — without this, a rejection without a `.message`
    // (e.g. a thrown string or a wasm-side abort) reaches the UI as
    // "Could not load the privacy filter: undefined" with no diagnostic.
    // eslint-disable-next-line no-console
    console.error("[pii] loadPii failed", e);
    throw e;
  } finally {
    loading = null;
  }
}

/**
 * Run inference on `text`, return the merged PII spans. `aggregation` is
 * the BIOES → span walker; before that we run a constrained Viterbi decode
 * over the per-token logits to clean up illegal tag transitions the raw
 * argmax would otherwise produce.
 */
export async function findSpans(text: string, id: VariantId): Promise<PiiSpan[]> {
  if (!text.trim()) return [];
  const filter = await loadPii(id);
  const { session, tokenizer, labels, ort: ortLib } = filter;

  // transformers.js's PreTrainedTokenizer call signature returns
  // { input_ids, attention_mask, token_type_ids?, offset_mapping? }
  // depending on the tokenizer config. We need offsets for span recovery.
  const enc = await tokenizer(text, { return_offsets: true } as Record<string, unknown>);
  type Tensorish = { data?: ArrayLike<number | bigint>; dims?: readonly number[] };
  const inputIdsT = (enc as Record<string, Tensorish>).input_ids;
  const attnT = (enc as Record<string, Tensorish>).attention_mask;
  if (!inputIdsT || !attnT) {
    throw new Error("Tokenizer did not return input_ids / attention_mask");
  }
  const inputIds = bigInt64ArrayFromTensorish(inputIdsT);
  const attn = bigInt64ArrayFromTensorish(attnT);
  const T = inputIds.length;
  if (T === 0) return [];

  // Try the tensor-style offset_mapping first; if absent (which is the
  // common case for the privacy-filter tokenizer, since transformers.js
  // falls back to the base `PreTrainedTokenizer` for it), reconstruct
  // offsets by decoding each token id back to a substring.
  let offsets: CharSpan[] = readOffsets(enc, T);
  if (offsets.every((o) => o.start === 0 && o.end === 0)) {
    offsets = offsetsByDecode(tokenizer, inputIds, attn, text);
  }

  // Build ORT tensors. Privacy-filter expects int64 input_ids +
  // attention_mask. token_type_ids isn't part of this model.
  const inputs: Record<string, ort.Tensor> = {
    input_ids: new ortLib.Tensor("int64", inputIds, [1, T]),
    attention_mask: new ortLib.Tensor("int64", attn, [1, T]),
  };

  const inputNames = session.inputNames;
  const filtered: Record<string, ort.Tensor> = {};
  for (const name of inputNames) {
    if (inputs[name]) filtered[name] = inputs[name];
  }

  const out = await session.run(filtered);
  const logitsTensor = out["logits"] ?? out[Object.keys(out)[0]];
  const logits = logitsTensor.data as Float32Array;
  const dims = logitsTensor.dims;
  const batch = dims[0];
  const seq = dims[1];
  const numLabels = dims[2];
  if (batch !== 1) throw new Error(`Unexpected batch dim ${batch}`);
  if (seq !== T) throw new Error(`Logits seq ${seq} != T ${T}`);
  if (numLabels !== labels.length) {
    throw new Error(`Logits labels ${numLabels} != labels.json length ${labels.length}`);
  }

  const path = viterbiDecode(logits, T, labels);
  const labelNames: string[] = [];
  for (let i = 0; i < T; i++) labelNames.push(labels[path[i]]);

  // attention_mask back into a number[] for the assembler — it doesn't
  // need bigint precision.
  const attnArr: number[] = new Array(T);
  for (let i = 0; i < T; i++) attnArr[i] = Number(attn[i]);
  return assembleSpans(labelNames, offsets, attnArr);
}

function bigInt64ArrayFromTensorish(t: { data?: ArrayLike<number | bigint> }): BigInt64Array {
  const data = t.data;
  if (!data) throw new Error("Tensor has no data");
  if (data instanceof BigInt64Array) return data;
  const out = new BigInt64Array(data.length);
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    out[i] = typeof v === "bigint" ? v : BigInt(v as number);
  }
  return out;
}

function readOffsets(enc: unknown, T: number): CharSpan[] {
  const e = enc as Record<string, { data?: ArrayLike<number | bigint>; dims?: readonly number[] }> & {
    offset_mapping?: { data?: ArrayLike<number | bigint>; dims?: readonly number[] };
    offsets?: Array<[number, number]>;
  };
  // 1) Tensor-style `offset_mapping` with shape [1, T, 2].
  const tensor = e.offset_mapping;
  if (tensor?.data && tensor.dims && tensor.dims.length === 3 && tensor.dims[2] === 2) {
    const data = tensor.data;
    const out: CharSpan[] = new Array(T);
    for (let i = 0; i < T; i++) {
      const a = data[i * 2];
      const b = data[i * 2 + 1];
      out[i] = { start: Number(a), end: Number(b) };
    }
    return out;
  }
  // 2) Plain `offsets: [[s,e], ...]`.
  if (Array.isArray(e.offsets)) {
    return e.offsets.map(([s, end]) => ({ start: Number(s), end: Number(end) }));
  }
  // 3) Fallback: nothing known.
  return new Array<CharSpan>(T).fill({ start: 0, end: 0 });
}

/**
 * Reconstruct character offsets by decoding each token id to its string and
 * scanning forward through the source text. The privacy-filter tokenizer
 * is loaded by transformers.js's base `PreTrainedTokenizer` (since its
 * model class is unrecognised), which doesn't surface `offset_mapping` —
 * so we rebuild it ourselves. Linear in `text.length * T`, but `T` is
 * always small (tokenized sequence length).
 */
function offsetsByDecode(
  tokenizer: PreTrainedTokenizer,
  inputIds: BigInt64Array,
  attentionMask: BigInt64Array,
  text: string,
): CharSpan[] {
  // Strip a leading BOS-ish whitespace marker some tokenizers add.
  const out: CharSpan[] = new Array(inputIds.length);
  let cursor = 0;
  for (let i = 0; i < inputIds.length; i++) {
    if (attentionMask[i] === 0n) {
      out[i] = { start: 0, end: 0 };
      continue;
    }
    const id = Number(inputIds[i]);
    let token: string;
    try {
      // skip_special_tokens=true so PAD/CLS/SEP collapse to "" and we
      // won't claim a char range for them.
      token = (tokenizer as unknown as { decode: (ids: number[], opts?: { skip_special_tokens?: boolean }) => string }).decode([id], { skip_special_tokens: true });
    } catch {
      token = "";
    }
    if (!token) {
      out[i] = { start: 0, end: 0 };
      continue;
    }
    // BPE / sentencepiece tokenizers emit a leading "Ġ" or "▁" or " "
    // marker for whitespace boundaries — `decode` already turns these
    // into a real space, so plain `indexOf` from the cursor works.
    const trimmed = token.replace(/^\s+/, "");
    if (!trimmed) {
      out[i] = { start: 0, end: 0 };
      cursor += token.length - trimmed.length;
      continue;
    }
    const idx = text.indexOf(trimmed, cursor);
    if (idx < 0) {
      // Couldn't relocate (e.g. byte-level BPE token straddling chars);
      // record an empty span so the assembler skips this token.
      out[i] = { start: 0, end: 0 };
      continue;
    }
    out[i] = { start: idx, end: idx + trimmed.length };
    cursor = idx + trimmed.length;
  }
  return out;
}
